import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PERK_ART } from "../lib/perkAssets";

/**
 * Sequential, centered "perk" celebration card. The Dashboard feeds a queue
 * and only ever passes the FIRST perk here, so cards appear ONE AT A TIME
 * (no more overlapping toasts). Tapping "Continue" pops the queue. A perk
 * flagged with `autoCloseMs` closes itself after that delay (and hides the
 * button) — used for the passive "Welcome back" greeting.
 *
 * perk = { key, type, title, subtitle, coins?, autoCloseMs? }
 */
export const PerkModal = ({ perk, remaining = 0, onNext }) => {
  const { t } = useTranslation();
  const art = perk ? PERK_ART[perk.type] || PERK_ART.streak : null;
  const autoMs = perk?.autoCloseMs ?? 3400; // every perk auto-advances "nicely"

  const onNextRef = React.useRef(onNext);
  useEffect(() => { onNextRef.current = onNext; }, [onNext]);
  useEffect(() => {
    if (!perk) return;
    const timer = setTimeout(() => onNextRef.current?.(), autoMs);
    return () => clearTimeout(timer);
  }, [perk?.key, autoMs, perk]);

  return (
    <AnimatePresence>
      {perk && (
        <motion.div
          data-testid="perk-modal"
          className="fixed inset-0 z-[70] grid place-items-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Dim backdrop — tap to advance */}
          <motion.div
            className="absolute inset-0 bg-[#1A1F18]/70 backdrop-blur-sm"
            onClick={onNext}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            key={perk.key}
            data-testid="perk-card"
            className="relative w-full max-w-[320px] rounded-[28px] bg-[#F7F5F0] border border-[#E6E2D8] shadow-2xl overflow-hidden"
            initial={{ scale: 0.82, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 12, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 20 }}
          >
            {/* Glow header */}
            <div className="relative pt-7 pb-3 flex flex-col items-center bg-gradient-to-b from-[#EFF1EC] to-[#F7F5F0]">
              <motion.div
                aria-hidden="true"
                className="absolute top-6 w-40 h-40 rounded-full"
                style={{ background: "radial-gradient(circle, rgba(232,163,101,0.35) 0%, rgba(232,163,101,0) 70%)" }}
                animate={{ scale: [1, 1.12, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.img
                src={art}
                alt=""
                aria-hidden="true"
                className="relative w-32 h-32 object-contain drop-shadow-[0_10px_24px_rgba(44,51,42,0.25)]"
                initial={{ scale: 0.6, rotate: -8 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.05 }}
              />
            </div>

            <div className="px-6 pb-6 text-center">
              <h3
                data-testid="perk-title"
                style={{ fontFamily: "Outfit, sans-serif" }}
                className="text-xl font-bold text-[#2C332A] leading-tight"
              >
                {perk.title}
              </h3>
              <p className="mt-2 text-sm text-[#2C332A]/70 leading-snug">{perk.subtitle}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PerkModal;
